import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AdminLayout from '../../components/AdminLayout';
import { API_BASE_URL } from '../../config/api';
import './AdminDashboardPage.css';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalProjects: 0,
    totalNews: 0
  });
  const [revenue, setRevenue] = useState({
    ads: 0,
    subscription: 0,
    membershipFee: 0
  });
  const [monthlyStats, setMonthlyStats] = useState({
    currentMonthRevenue: 0,
    newSuppliers: 0,
    newSpecialists: 0
  });
  const [yearlyChart, setYearlyChart] = useState([]);
  const [supplierRequests, setSupplierRequests] = useState([]);
  const [filteredSupplierRequests, setFilteredSupplierRequests] = useState([]);
  const [productRequests, setProductRequests] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
  });
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllSuppliers, setSelectAllSuppliers] = useState(false);
  const [selectAllProducts, setSelectAllProducts] = useState(false);
  const [companyTypeFilter, setCompanyTypeFilter] = useState('All');
  const [showCompanyTypeDropdown, setShowCompanyTypeDropdown] = useState(false);

  const companyTypes = ['All', 'Manufacturer', 'Supplier', 'Distributor', 'Trader', 'Wholesaler'];

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Parse selectedMonth to get month/year for filtering
      const [monthStr, yearStr] = selectedMonth.split(', ');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.indexOf(monthStr);
      const year = parseInt(yearStr);

      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, suppliersRes, productsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/stats?month=${monthIndex + 1}&year=${year}`),
        fetch(`${API_BASE_URL}/dashboard/admin/supplier-requests?month=${monthIndex + 1}&year=${year}`, { headers: authHeaders }),
        fetch(`${API_BASE_URL}/dashboard/admin/product-requests?month=${monthIndex + 1}&year=${year}`, { headers: authHeaders })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats({
          totalUsers: statsData.users?.total || 0,
          totalProducts: statsData.products?.total || 0,
          totalProjects: statsData.projects || 0,
          totalNews: statsData.news || 0
        });
        if (statsData.revenue) {
          setRevenue({
            ads: statsData.revenue.allTimeAds || statsData.revenue.ads || 0,
            subscription: statsData.revenue.allTimeSubscription || statsData.revenue.subscription || 0,
            membershipFee: statsData.revenue.allTimeMembership || statsData.revenue.membership || 0
          });
        }
        if (statsData.monthly || statsData.today) {
          const src = statsData.monthly || statsData.today;
          setMonthlyStats({
            currentMonthRevenue: statsData.revenue?.currentMonth || 0,
            newSuppliers: src.newSuppliers || 0,
            newSpecialists: src.newSpecialists || 0
          });
        }
        if (Array.isArray(statsData.chartData)) {
          setYearlyChart(statsData.chartData);
        }
        setError('');
      } else {
        setError('Failed to load dashboard data');
      }

      if (suppliersRes.ok) {
        const suppliersData = await suppliersRes.json();
        if (Array.isArray(suppliersData)) {
          setSupplierRequests(suppliersData);
          setFilteredSupplierRequests(suppliersData);
        }
      }

      if (productsRes.ok) {
        const productsData = await productsRes.json();
        if (Array.isArray(productsData)) {
          setProductRequests(productsData);
        }
      }
    } catch (err) {
      console.error('Error fetching admin dashboard data:', err);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh stats every 30 seconds so counts stay up-to-date
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Re-fetch when tab/window regains focus (e.g., admin returns from another page)
  useEffect(() => {
    const handleFocus = () => {
      fetchDashboardData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchDashboardData]);

  // Track mobile view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter suppliers when company type changes
  useEffect(() => {
    if (companyTypeFilter === 'All') {
      setFilteredSupplierRequests(supplierRequests);
    } else {
      const filtered = supplierRequests.filter(s => 
        s.type?.toLowerCase() === companyTypeFilter.toLowerCase()
      );
      setFilteredSupplierRequests(filtered);
    }
  }, [companyTypeFilter, supplierRequests]);

  // Handle company type filter
  const handleCompanyTypeFilter = (type) => {
    setCompanyTypeFilter(type);
    setShowCompanyTypeDropdown(false);
  };

  // Helper function to get product color class based on product name/category
  const getProductColorClass = (productName) => {
    const name = (productName || '').toLowerCase();
    if (name.includes('construction') || name.includes('material')) return 'blue';
    if (name.includes('industrial') || name.includes('equipment')) return 'green';
    if (name.includes('machinery') || name.includes('parts')) return 'red';
    if (name.includes('factory') || name.includes('supplies')) return 'yellow';
    // Default color based on first letter hash
    const colors = ['blue', 'green', 'red', 'yellow'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Handle supplier checkbox
  const handleSupplierSelect = (id) => {
    const strId = String(id);
    setSelectedSuppliers(prev => 
      prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
    );
  };

  // Handle product checkbox
  const handleProductSelect = (id) => {
    const strId = String(id);
    setSelectedProducts(prev => 
      prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
    );
  };

  // Handle select all suppliers
  const handleSelectAllSuppliers = () => {
    if (selectAllSuppliers) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(filteredSupplierRequests.map(s => String(s.id)));
    }
    setSelectAllSuppliers(!selectAllSuppliers);
  };

  // Handle select all products
  const handleSelectAllProducts = () => {
    if (selectAllProducts) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(productRequests.map(p => String(p.id)));
    }
    setSelectAllProducts(!selectAllProducts);
  };

  // Handle bulk approve suppliers
  const handleBulkApproveSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Please select at least one supplier to approve');
      return;
    }
    if (!window.confirm(`Are you sure you want to approve ${selectedSuppliers.length} supplier(s)?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedSuppliers) {
        const supplierId = String(id).startsWith('seller-') ? id.replace('seller-', '') : id;
        const response = await fetch(`${API_BASE_URL}/sellers/${supplierId}/approve`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      alert(`Approved: ${successCount}, Failed: ${failCount}`);
      setSelectedSuppliers([]);
      setSelectAllSuppliers(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error bulk approving suppliers:', error);
      alert('Error approving suppliers');
    }
  };

  // Handle bulk decline suppliers
  const handleBulkDeclineSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Please select at least one supplier to decline');
      return;
    }
    if (!window.confirm(`Are you sure you want to decline ${selectedSuppliers.length} supplier(s)?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedSuppliers) {
        const supplierId = String(id).startsWith('seller-') ? id.replace('seller-', '') : id;
        const response = await fetch(`${API_BASE_URL}/sellers/${supplierId}/decline`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      alert(`Declined: ${successCount}, Failed: ${failCount}`);
      setSelectedSuppliers([]);
      setSelectAllSuppliers(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error bulk declining suppliers:', error);
      alert('Error declining suppliers');
    }
  };

  // Handle bulk approve products
  const handleBulkApproveProducts = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to approve');
      return;
    }
    if (!window.confirm(`Are you sure you want to approve ${selectedProducts.length} product(s)?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedProducts) {
        const response = await fetch(`${API_BASE_URL}/products/${id}/approve`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      alert(`Approved: ${successCount}, Failed: ${failCount}`);
      setSelectedProducts([]);
      setSelectAllProducts(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error bulk approving products:', error);
      alert('Error approving products');
    }
  };

  // Handle bulk decline products
  const handleBulkDeclineProducts = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to decline');
      return;
    }
    if (!window.confirm(`Are you sure you want to decline ${selectedProducts.length} product(s)?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedProducts) {
        const response = await fetch(`${API_BASE_URL}/products/${id}/decline`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      alert(`Declined: ${successCount}, Failed: ${failCount}`);
      setSelectedProducts([]);
      setSelectAllProducts(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error bulk declining products:', error);
      alert('Error declining products');
    }
  };

  // Handle approve supplier
  const handleApproveSupplier = async (supplier) => {
    try {
      // Extract numeric ID from seller-{id} format or use as-is if numeric
      const supplierId = String(supplier.id).startsWith('seller-') 
        ? supplier.id.replace('seller-', '') 
        : supplier.id;
      
      const response = await fetch(`${API_BASE_URL}/sellers/${supplierId}/approve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        alert(`${supplier.name || 'Supplier'} approved successfully!`);
        fetchDashboardData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to approve supplier');
      }
    } catch (error) {
      console.error('Error approving supplier:', error);
      alert('Error approving supplier');
    }
  };

  // Handle decline supplier
  const handleDeclineSupplier = async (supplier) => {
    if (!window.confirm(`Are you sure you want to decline ${supplier.name || 'this supplier'}?`)) return;
    try {
      // Extract numeric ID from seller-{id} format or use as-is if numeric
      const supplierId = String(supplier.id).startsWith('seller-') 
        ? supplier.id.replace('seller-', '') 
        : supplier.id;
      
      const response = await fetch(`${API_BASE_URL}/sellers/${supplierId}/decline`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        alert(`${supplier.name || 'Supplier'} declined`);
        fetchDashboardData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to decline supplier');
      }
    } catch (error) {
      console.error('Error declining supplier:', error);
      alert('Error declining supplier');
    }
  };

  // Handle approve product
  const handleApproveProduct = async (product) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${product.id}/approve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        alert(`Product approved successfully!`);
        fetchDashboardData();
      } else {
        alert('Failed to approve product');
      }
    } catch (error) {
      console.error('Error approving product:', error);
      alert('Error approving product');
    }
  };

  // Handle decline product
  const handleDeclineProduct = async (product) => {
    if (!window.confirm(`Are you sure you want to decline this product?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/products/${product.id}/decline`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        alert(`Product declined`);
        fetchDashboardData();
      } else {
        alert('Failed to decline product');
      }
    } catch (error) {
      console.error('Error declining product:', error);
      alert('Error declining product');
    }
  };

  // Navigate to supplier details
  const handleSupplierDetails = (supplier) => {
    navigate(`/admin/suppliers`, { state: { selectedSupplier: supplier } });
  };

  // Navigate to product details
  const handleProductDetails = (product) => {
    navigate(`/admin/products`, { state: { selectedProduct: product } });
  };

  // Handle stat card clicks
  const handleStatClick = (type) => {
    switch(type) {
      case 'users':
        navigate('/admin/users');
        break;
      case 'products':
        navigate('/admin/products');
        break;
      case 'projects':
        navigate('/admin/projects');
        break;
      case 'news':
        navigate('/admin/news/add');
        break;
      default:
        break;
    }
  };

  // Month selector options - generate for 2025 and 2026
  const months = [
    'Jan, 2025', 'Feb, 2025', 'Mar, 2025', 'Apr, 2025', 'May, 2025', 'Jun, 2025',
    'Jul, 2025', 'Aug, 2025', 'Sep, 2025', 'Oct, 2025', 'Nov, 2025', 'Dec, 2025',
    'Jan, 2026', 'Feb, 2026', 'Mar, 2026', 'Apr, 2026', 'May, 2026', 'Jun, 2026',
    'Jul, 2026', 'Aug, 2026', 'Sep, 2026', 'Oct, 2026', 'Nov, 2026', 'Dec, 2026'
  ];

  return (
    <AdminLayout>
      <div className="dashboard-content">
        {error && (
          <div className="dashboard-error-banner">
            <span>{error}</span>
            <button onClick={fetchDashboardData}>Retry</button>
          </div>
        )}
        {/* Grey Header Section with Title + 4 Stats */}
        <div className="dashboard-header-section">
          <div className="page-header">
            <h1>Admin Dashboard</h1>
            <p>Centralized control to manage users, content, and platform operations.</p>
          </div>

          {/* Stats Cards Row - 4 separate white cards */}
          <div className="stats-row">
            <div className="stats-cards">
              <StatCard 
                icon="users" 
                label="Total Users" 
                value={loading ? '...' : stats.totalUsers} 
              />
              <StatCard 
                icon="products" 
                label="Total Products Listing" 
                value={loading ? '...' : stats.totalProducts} 
                onClick={() => handleStatClick('products')}
              />
              <StatCard 
                icon="projects" 
                label="Total Projects Listing" 
                value={loading ? '...' : stats.totalProjects} 
                onClick={() => handleStatClick('projects')}
              />
              <StatCard 
                icon="news" 
                label="Total News Listing" 
                value={loading ? '...' : stats.totalNews} 
                onClick={() => handleStatClick('news')}
              />
            </div>
          </div>
        </div>

        {/* Dashboard Box - contains revenue + chart */}
        <div className="dashboard-chart-box">
          {/* Revenue Stats Row with Sort by */}
          <div className="revenue-stats-row">
            <div className="revenue-stats">
              <div className="revenue-stat-item" onClick={() => navigate('/admin/ads')} style={{background: 'transparent', border: 'none', boxShadow: 'none', cursor: 'pointer'}}>
                <div className="revenue-icon blue">$</div>
                <div className="revenue-info">
                  <span className="revenue-label">Earning from Ads</span>
                  <span className="revenue-value">{loading ? '...' : error ? 'N/A' : `$${revenue.ads.toLocaleString()}`}</span>
                </div>
              </div>
              <div className="revenue-stat-item" onClick={() => navigate('/admin/membership')} style={{background: 'transparent', border: 'none', boxShadow: 'none', cursor: 'pointer'}}>
                <div className="revenue-icon orange">$</div>
                <div className="revenue-info">
                  <span className="revenue-label">Earning from subscription</span>
                  <span className="revenue-value">{loading ? '...' : error ? 'N/A' : `$${revenue.subscription.toLocaleString()}`}</span>
                </div>
              </div>
              <div className="revenue-stat-item" onClick={() => navigate('/admin/payments')} style={{background: 'transparent', border: 'none', boxShadow: 'none', cursor: 'pointer'}}>
                <div className="revenue-icon green">$</div>
                <div className="revenue-info">
                  <span className="revenue-label">Earning from Membership fee</span>
                  <span className="revenue-value">{loading ? '...' : error ? 'N/A' : `$${revenue.membershipFee.toLocaleString()}`}</span>
                </div>
              </div>
            </div>
            <div className="date-filter">
              <span className="sort-label">Sort by</span>
              <div className="date-selector">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.66667 1.66667V4.16667M13.3333 1.66667V4.16667M2.91667 7.575H17.0833M17.5 7.08333V14.1667C17.5 16.6667 16.25 18.3333 13.3333 18.3333H6.66667C3.75 18.3333 2.5 16.6667 2.5 14.1667V7.08333C2.5 4.58333 3.75 2.91667 6.66667 2.91667H13.3333C16.25 2.91667 17.5 4.58333 17.5 7.08333Z" stroke="#7C7D7F" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <select 
                  value={selectedMonth} 
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="month-select"
                >
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Chart Section */}
          <div className="chart-section">
            <div className="chart-sidebar">
              <div className="chart-header">
                <h3>Dashboard</h3>
                <p>Overview of {selectedMonth}</p>
              </div>
              <div className="chart-stats">
                <div className="chart-stat">
                  <span className="chart-stat-value blue">${monthlyStats.currentMonthRevenue.toLocaleString()}</span>
                  <span className="chart-stat-label">Current Month Revenue</span>
                </div>
                <div className="chart-stat-row">
                  <div className="chart-stat-small">
                    <span className="chart-stat-num">{monthlyStats.newSuppliers}</span>
                    <span className="chart-stat-text">New Suppliers Join</span>
                  </div>
                  <div className="chart-stat-small">
                    <span className="chart-stat-num">{monthlyStats.newSpecialists}</span>
                    <span className="chart-stat-text">New Specialists Join</span>
                  </div>
                </div>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <span className="legend-dot blue"></span>
                  <span>Membership</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot green"></span>
                  <span>Ads</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot yellow"></span>
                  <span>Verification</span>
                </div>
              </div>
            </div>
            <DashboardChart yearlyChart={yearlyChart} loading={loading} />
          </div>
        </div>

        {/* Supplier Requests Table */}
        <div className="table-section">
          <div className="table-header mobile-table-header">
            <h2>Today's New Supplier & Specialists Request</h2>
            <div className="table-header-actions">
              <div className="bulk-actions">
                <button 
                  className="bulk-action-btn approve-btn" 
                  onClick={handleBulkApproveSuppliers}
                  disabled={selectedSuppliers.length === 0}
                  style={{ opacity: selectedSuppliers.length === 0 ? 0.5 : 1 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Approve {selectedSuppliers.length > 0 ? `(${selectedSuppliers.length})` : ''}
                </button>
                <button 
                  className="bulk-action-btn decline-btn" 
                  onClick={handleBulkDeclineSuppliers}
                  disabled={selectedSuppliers.length === 0}
                  style={{ opacity: selectedSuppliers.length === 0 ? 0.5 : 1 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Decline {selectedSuppliers.length > 0 ? `(${selectedSuppliers.length})` : ''}
                </button>
              </div>
              <span className="see-all mobile-see-all" onClick={() => navigate('/admin/suppliers')}>See All</span>
            </div>
          </div>
          <div className="table-wrapper" style={{overflowX: 'auto', width: '100%'}}>
            <table className="data-table" style={{width: '100%', minWidth: '900px', tableLayout: 'fixed', fontSize: '12px'}}>
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <div className="checkbox-wrapper">
                      <input 
                        type="checkbox" 
                        checked={selectAllSuppliers}
                        onChange={handleSelectAllSuppliers}
                      />
                      <span>All</span>
                    </div>
                  </th>
                  <th>Logo</th>
                  <th>Creation date</th>
                  <th>Name</th>
                  <th className="company-type-header">
                    <div className="dropdown-header" onClick={() => setShowCompanyTypeDropdown(!showCompanyTypeDropdown)}>
                      <span>Company Type</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showCompanyTypeDropdown && (
                      <div className="company-type-dropdown">
                        {companyTypes.map((type) => (
                          <div 
                            key={type} 
                            className={`dropdown-item ${companyTypeFilter === type ? 'active' : ''}`}
                            onClick={() => handleCompanyTypeFilter(type)}
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                  <th>User type</th>
                  <th>Location</th>
                  <th>Gmail</th>
                  <th>Membership Plan</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="loading-cell">Loading...</td>
                  </tr>
                ) : filteredSupplierRequests.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-cell">No supplier requests for this period{companyTypeFilter !== 'All' ? ` (${companyTypeFilter})` : ''}</td>
                  </tr>
                ) : (
                  filteredSupplierRequests.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedSuppliers.includes(String(item.id))}
                          onChange={() => handleSupplierSelect(item.id)}
                        />
                      </td>
                      <td>
                        <img 
                          src={item.logo || '/uploads/builder-images/22ad783b04f12eeb837317983b486fdd87f7a976_w72.png'} 
                          alt="Company" 
                          className="company-logo" 
                        />
                      </td>
                      <td>{item.date || '12,06,2024'}</td>
                      <td>{item.name || 'Office Equipments Company'}</td>
                      <td>{item.type || 'Manufacture'}</td>
                      <td>{item.userType || 'Supplier'}</td>
                      <td>{item.location || 'Dubai'}</td>
                      <td>{item.email || 'alexander@gmail.com'}</td>
                      <td>
                        <span className={`membership-badge ${(item.plan || 'Basic').toLowerCase().replace(' ', '-')}`}>
                          {item.plan || 'Basic'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <span 
                          className="details-link"
                          onClick={() => navigate(`/admin/suppliers/${item.id}`)}
                          style={{ color: '#2E5BBA', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Details
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Product Requests Table */}
        <div className="table-section">
          <div className="table-header mobile-table-header">
            <h2>Today's Product Request</h2>
            <div className="table-header-actions">
              <div className="bulk-actions">
                <button 
                  className="bulk-action-btn approve-btn" 
                  onClick={handleBulkApproveProducts}
                  disabled={selectedProducts.length === 0}
                  style={{ opacity: selectedProducts.length === 0 ? 0.5 : 1 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Approve {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                </button>
                <button 
                  className="bulk-action-btn decline-btn" 
                  onClick={handleBulkDeclineProducts}
                  disabled={selectedProducts.length === 0}
                  style={{ opacity: selectedProducts.length === 0 ? 0.5 : 1 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Decline {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                </button>
              </div>
              <span className="see-all mobile-see-all" onClick={() => navigate('/admin/products')}>See All</span>
            </div>
          </div>
          <div className="table-wrapper">
            <table className="data-table product-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <div className="checkbox-wrapper">
                      <input 
                        type="checkbox" 
                        checked={selectAllProducts}
                        onChange={handleSelectAllProducts}
                      />
                    </div>
                  </th>
                  <th></th>
                  <th>Product Name</th>
                  <th>Company/Business</th>
                  <th>Price</th>
                  <th>Membership Plan</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="loading-cell">Loading...</td>
                  </tr>
                ) : productRequests.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">No product requests for this period</td>
                  </tr>
                ) : (
                  productRequests.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedProducts.includes(String(item.id))}
                          onChange={() => handleProductSelect(item.id)}
                        />
                      </td>
                      <td>
                        <span className={`product-color-indicator ${getProductColorClass(item.title || item.name)}`}></span>
                      </td>
                      <td className="product-name-cell">{item.title || item.name || 'Product'}</td>
                      <td className="company-name-cell">{item.company || 'Unknown Company'}</td>
                      <td className="price-cell">{item.price || '$0.00'}</td>
                      <td>
                        <span className={`membership-badge ${(item.membershipPlan || item.plan || 'Basic').toLowerCase().replace(' ', '-')}`}>
                          {item.membershipPlan || item.plan || 'Basic'}
                        </span>
                      </td>
                      <td className="action-cell">
                        {item.status === 'Pending' ? (
                          <span 
                            className="pending-link"
                            onClick={() => handleApproveProduct(item)}
                            style={{ 
                              color: '#E8AE48', 
                              cursor: 'pointer', 
                              fontWeight: '500'
                            }}
                            title="Click to approve this product"
                          >
                            Pending
                          </span>
                        ) : (
                          <span 
                            style={{ 
                              color: item.status === 'Published' ? '#22C55E' : '#64748B', 
                              fontWeight: '500'
                            }}
                          >
                            {item.status || 'Unknown'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// Dynamic Chart Component using Recharts
const DashboardChart = ({ yearlyChart, loading }) => {
  const defaultData = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    .map(m => ({ month: m, membership: 0, ads: 0, verification: 0, newSuppliers: 0, newSpecialists: 0 }));
  const data = yearlyChart && yearlyChart.length > 0 ? yearlyChart : defaultData;

  const hasRevenue = data.some(d => (d.membership || 0) > 0 || (d.ads || 0) > 0 || (d.verification || 0) > 0);
  const hasActivity = data.some(d => (d.newSuppliers || 0) > 0 || (d.newSpecialists || 0) > 0);

  if (loading) {
    return (
      <div className="chart-container">
        <div className="chart-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="chart-loading-spinner">Loading chart data...</div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '10px 14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: '13px'
        }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#1F2937' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '2px 0', color: entry.color }}>
              {entry.name}: {hasRevenue ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <div className="chart-area">
        {!hasRevenue && !hasActivity && (
          <div className="chart-no-data">
            <span>No data available for this period</span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          {hasRevenue ? (
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="membershipGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="adsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#79C889" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#79C889" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="verificationGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E8AE48" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#E8AE48" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#636F85', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#636F85', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={formatCurrency} width={50} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="membership" name="Membership" stroke="#3B82F6" strokeWidth={2.5} fill="url(#membershipGrad)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
              <Area type="monotone" dataKey="ads" name="Ads" stroke="#79C889" strokeWidth={2} fill="url(#adsGrad)" dot={false} />
              <Area type="monotone" dataKey="verification" name="Verification" stroke="#E8AE48" strokeWidth={2} fill="url(#verificationGrad)" dot={false} />
            </AreaChart>
          ) : hasActivity ? (
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="suppliersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="specialistsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#79C889" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#79C889" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#636F85', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#636F85', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="newSuppliers" name="New Suppliers" stroke="#3B82F6" strokeWidth={2.5} fill="url(#suppliersGrad)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
              <Area type="monotone" dataKey="newSpecialists" name="New Specialists" stroke="#79C889" strokeWidth={2} fill="url(#specialistsGrad)" dot={{ r: 3, fill: '#79C889' }} activeDot={{ r: 5 }} />
            </AreaChart>
          ) : (
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#636F85', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#636F85', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, onClick }) => {
  const icons = {
    users: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    products: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 16V8C20.9996 7.6481 20.9071 7.30257 20.7316 6.99876C20.5561 6.69496 20.3038 6.44368 20 6.27L13 2.27C12.696 2.09627 12.3511 2.00488 12 2.00488C11.6489 2.00488 11.304 2.09627 11 2.27L4 6.27C3.69626 6.44368 3.44398 6.69496 3.26846 6.99876C3.09294 7.30257 3.00036 7.6481 3 8V16C3.00036 16.3519 3.09294 16.6974 3.26846 17.0012C3.44398 17.305 3.69626 17.5563 4 17.73L11 21.73C11.304 21.9037 11.6489 21.9951 12 21.9951C12.3511 21.9951 12.696 21.9037 13 21.73L20 17.73C20.3038 17.5563 20.5561 17.305 20.7316 17.0012C20.9071 16.6974 20.9996 16.3519 21 16Z" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3.27 6.96L12 12.01L20.73 6.96" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 22.08V12" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    projects: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    news: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2V8H20" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 13H8" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 17H8" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 9H9H8" stroke="#003B5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  };

  return (
    <div className={`admin-stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick} style={{background: 'transparent', border: 'none', boxShadow: 'none'}}>
      <div className="stat-icon">
        {icons[icon] || icons.users}
      </div>
      <div className="stat-info">
        <span className="stat-label">{label}</span>
        <span className="stat-value">{value}</span>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
